/*
 * Hands the thread back to the task queue and resumes on the next turn of it.
 *
 * Through a message rather than a timer: a setTimeout(0) is floored at 4ms once
 * the timers nest, which they do from the second call onwards, so a loop pausing
 * every eight milliseconds was buying each pause at half the price of the work it
 * had just done. A message lands on the same queue — the browser still gets its
 * chance to paint between tasks — with no floor under it.
 *
 * One channel for the page rather than one per caller: the queue is FIFO and
 * every waiter posts its own message, so loops running side by side each get
 * exactly the one wake-up they asked for.
 */

/*
 * Node counts a port with a listener as a reason to keep its event loop alive, so
 * a channel left listening would hold open any script that so much as imported
 * this module — and one left unreferenced would let a script exit between posting
 * the message and receiving it, stranding the caller mid-yield. The port is
 * therefore held only while a yield is outstanding. A browser has neither ref nor
 * unref and needs neither: its event loop outlives the page.
 */
type TypeHoldablePort = MessagePort & { ref?: () => void; unref?: () => void };

let channel: MessageChannel | null = null;
const waiting: (() => void)[] = [];

// ----------------------------------------------------------------------------

function getChannel(): MessageChannel {
    if (channel == null) {
        const next = new MessageChannel();

        next.port1.onmessage = (): void => {
            waiting.shift()?.();

            if (waiting.length === 0) {
                (next.port1 as TypeHoldablePort).unref?.();
            }
        };

        (next.port1 as TypeHoldablePort).unref?.();

        channel = next;
    }

    return channel;
}

// ----------------------------------------------------------------------------

async function yieldToTaskQueue(): Promise<void> {
    if (typeof MessageChannel !== 'function') {
        await new Promise<void>((resolve): void => {
            setTimeout(resolve, 0);
        });

        return;
    }

    const current = getChannel();

    await new Promise<void>((resolve): void => {
        waiting.push(resolve);
        (current.port1 as TypeHoldablePort).ref?.();
        current.port2.postMessage(undefined);
    });
}

// ----------------------------------------------------------------------------

export default yieldToTaskQueue;
